import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ReactFlow,
    Background,
    Controls,
    ConnectionMode,
    useNodesState,
    useEdgesState,
    addEdge,
    Connection,
    Node,
    ReactFlowProvider,
    useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useToast } from '@/hooks/use-toast';
import {
    createNode,
    updateNode,
    deleteNode,
    getFunnelDetails,
    validateFunnel,
    createTransition,
    deleteTransition
} from '@/services/api';
import { createButton } from '@/services/api/nodes';
import { getPageTranslations } from '@/services/api/funnel';

import TriggerNode from './components/nodes/TriggerNode';
import MessageNode from './components/nodes/MessageNode';
import DelayNode from './components/nodes/DelayNode';
import MessageTemplateNode from './components/nodes/MessageTemplateNode';
import PhotoTemplateNode from './components/nodes/PhotoTemplateNode.tsx';
import VideoTemplateNode from './components/nodes/VideoTemplateNode';
import PhotoNode from './components/nodes/PhotoNode';
import AudioNode from './components/nodes/AudioNode';
import VideoNode from './components/nodes/VideoNode';
import TagNode from './components/nodes/TagNode';
import GoToNode from './components/nodes/GoToNode';
import ReplyNode from './components/nodes/ReplyNode';
import WaitClickNode from './components/nodes/WaitClickNode';
import Sidebar from './components/Sidebar';
import Toolbar from './components/Toolbar';
import NodePanel from './components/NodePanel';
import FlowPreviewModal from './components/FlowPreviewModal';

import {
    FlowNodeData, NodeResponseFromAPI,
    transformNodeToFlowNode,
    transformTransitionToFlowEdge,
    TransitionResponseFromAPI
} from '@/types/flow';
import type { FlowNode, FlowEdge } from '@/types/flow';
import type { NodeCreate } from '@/types/node';
import CustomEdge from "@/pages/Meta/Flow/Builder/components/edges/CustomEdge.tsx";
import { nodeNames, nodeConfigs } from './constants/nodeCategories';
import type { NodeType } from './constants/nodeCategories';
import { commentAutoReplyNodeCategories } from './constants/commentAutoReplyNodeCategories';
import type { CommentAutoReply } from '@/services/api/comment-auto-replies';

const nodeTypes = {
    trigger: TriggerNode,
    message: MessageNode,
    message_template: MessageTemplateNode,
    photo: PhotoNode,
    photo_template: PhotoTemplateNode,
    video: VideoNode,
    video_template: VideoTemplateNode,
    audio: AudioNode,
    delay: DelayNode,
    tag: TagNode,
    go_to: GoToNode,
    reply: ReplyNode,
    wait_click: WaitClickNode,
};

const edgeTypes = {
    custom: CustomEdge,
};


export interface FlowBuilderProps {
    builderMode?: 'flow' | 'comment_auto_reply';
    externalFunnelId?: number;
    ruleData?: CommentAutoReply;
    onBack?: () => void;
}

// Componente INTERNO (com useReactFlow)
function FlowBuilderInner({ builderMode = 'flow', externalFunnelId, ruleData, onBack }: FlowBuilderProps) {
    const params = useParams<{ funnelId: string }>();
    const funnelId = externalFunnelId ? String(externalFunnelId) : params.funnelId;
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const reactFlowInstance = useReactFlow();

    const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const clipboardRef = useRef<FlowNodeData | null>(null);
    const selectedNodeRef = useRef<FlowNode | null>(null);
    const nodesRef = useRef<FlowNode[]>([]);

    // Modo "Visualizar tradução": quando setado, sobrepõe os textos master
    // pelas traduções da página. Read-only — pra editar, sair da visualização.
    const [viewingPageId, setViewingPageId] = useState<string | null>(null);

    // Query para buscar detalhes do funil
    const { data: funnelData, isLoading } = useQuery({
        queryKey: ['funnel-details', funnelId],
        queryFn: () => getFunnelDetails(Number(funnelId)),
        enabled: !!funnelId,
    });

    // Query das traduções da página em visualização (só roda se viewingPageId setado)
    const { data: pageTranslations } = useQuery({
        queryKey: ['funnel-page-translations', funnelId, viewingPageId],
        queryFn: () => getPageTranslations(Number(funnelId), viewingPageId!),
        enabled: !!funnelId && !!viewingPageId,
    });

    const isViewing = !!viewingPageId && !!pageTranslations;
    const viewingPage = funnelData?.pages.find((p) => p.page_id === viewingPageId);
    // Mutation para criar node
    const createNodeMutation = useMutation({
        mutationFn: ({ data }: { data: NodeCreate }) => createNode(Number(funnelId), data),
        onSuccess: () => {
            toast({
                title: 'Node criado!',
                description: 'O node foi criado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar node',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para atualizar posição do node
    const updateNodeMutation = useMutation({
        mutationFn: ({ nodeId, data }: { nodeId: number; data: any }) => updateNode(nodeId, data),
    });

    // Mutation para deletar node
    const deleteNodeMutation = useMutation({
        mutationFn: deleteNode,
        onSuccess: () => {
            toast({
                title: 'Node excluído!',
                description: 'O node foi excluído com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
            setSelectedNode(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao excluir node',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para criar transição
    const createTransitionMutation = useMutation({
        mutationFn: ({ from_node_id, to_node_id, to_button_id }: {
            from_node_id: number;
            to_node_id: number;
            to_button_id?: number | null;
        }) => createTransition({ from_node_id, to_node_id, to_button_id }),
        onSuccess: () => {
            toast({
                title: 'Conexão criada!',
                description: 'Os nodes foram conectados com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao conectar',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar a conexão.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
    });

    // Mutation para deletar transição
    const deleteTransitionMutation = useMutation({
        mutationFn: deleteTransition,
        onSuccess: () => {
            toast({
                title: 'Conexão removida!',
                description: 'A conexão foi removida com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao remover conexão',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handler para duplicar node
    const handleDuplicateNode = useCallback(async (nodeId: number) => {
        const node = nodesRef.current.find((n) => (n.data as FlowNodeData).id === nodeId);
        if (!node || !funnelId) return;

        const data = node.data as FlowNodeData;
        try {
            const newNode = await createNode(Number(funnelId), {
                node_type: data.node_type,
                node_name: `${data.node_name} (cópia)`,
                position_x: (node.position.x ?? 0) + 40,
                position_y: (node.position.y ?? 0) + 40,
                config: data.config || {},
            });

            if (Object.keys(data.config || {}).length > 0) {
                await updateNode(newNode.id, { config: data.config });
            }

            if (data.buttons?.length) {
                for (const btn of data.buttons) {
                    await createButton(newNode.id, {
                        button_text: btn.button_text,
                        button_type: btn.button_type,
                        button_value: btn.button_value ?? btn.button_text,
                        button_order: btn.position ?? btn.button_order ?? 0,
                        url: btn.url,
                    });
                }
            }

            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
            toast({ title: 'Node duplicado!' });
        } catch (error: any) {
            toast({
                variant: 'destructive',
                title: 'Erro ao duplicar node',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        }
    }, [funnelId, queryClient, toast]);

    // Handler para editar node
    const handleEditNode = useCallback((nodeId: number) => {
        const node = nodes.find((n) => (n.data as FlowNodeData).id === nodeId);
        if (node) {
            setSelectedNode(node);
        }
    }, [nodes]);

    // Handler para deletar node
    const handleDeleteNode = useCallback(
        (nodeId: number) => {
            if (confirm('Tem certeza que deseja deletar este node?')) {
                deleteNodeMutation.mutate(nodeId);
            }
        },
        [deleteNodeMutation]
    );

    // Carregar nodes e edges quando os dados chegarem
    useEffect(() => {
        if (funnelData) {
            const flowNodes: FlowNode[] = funnelData.nodes.map((node) => {
                const flowNode = transformNodeToFlowNode(node as unknown as NodeResponseFromAPI);

                // Overlay de tradução (read-only): se estiver visualizando uma página,
                // substitui message/title/subtitle/buttons pelo conteúdo traduzido,
                // caindo no master quando a tradução não existe pra aquele node.
                let configWithTranslation = flowNode.data.config;
                if (isViewing && pageTranslations) {
                    const t = pageTranslations.nodes[String(node.id)];
                    if (t) {
                        configWithTranslation = {
                            ...configWithTranslation,
                            ...(t.message ? { message: t.message } : {}),
                            ...(t.title ? { title: t.title } : {}),
                            ...(t.subtitle ? { subtitle: t.subtitle } : {}),
                        };
                    }
                    if (configWithTranslation.buttons) {
                        configWithTranslation = {
                            ...configWithTranslation,
                            buttons: configWithTranslation.buttons.map((b) => {
                                const bt = pageTranslations.buttons[String(b.id)];
                                return bt?.button_text ? { ...b, button_text: bt.button_text } : b;
                            }),
                        };
                    }
                }

                return {
                    ...flowNode,
                    draggable: !isViewing,
                    data: {
                        ...flowNode.data,
                        config: configWithTranslation,
                        tag_nome: (node as any).tag_nome,
                        tag_cor: (node as any).tag_cor,
                        // Em modo visualização, desabilita callbacks de edição/exclusão.
                        onEdit: isViewing ? undefined : handleEditNode,
                        onDelete: isViewing ? undefined : handleDeleteNode,
                        onDuplicate: isViewing ? undefined : handleDuplicateNode,
                    },
                };
            });

            const flowEdges: FlowEdge[] = funnelData.transitions.map((transition) => {
                // Cast para o tipo esperado
                const edge = transformTransitionToFlowEdge(
                    transition as unknown as TransitionResponseFromAPI,
                    funnelData.nodes as unknown as NodeResponseFromAPI[]
                );
                return {
                    ...edge,
                    type: 'custom',
                    data: {
                        ...edge.data,
                        onDelete: (transitionId: number) => {
                            deleteTransitionMutation.mutate(transitionId);
                        },
                    },
                };
            });


            setNodes(flowNodes);
            setEdges(flowEdges);
        }
    }, [funnelData, setNodes, setEdges, isViewing, pageTranslations]);


    // Handler para conectar nodes
    const onConnect = useCallback(
        (connection: Connection) => {
            if (!connection.source || !connection.target) return;

            // ✅ NOVA LÓGICA: Detectar se a conexão vem de um botão
            let toButtonId: number | null = null;

            if (connection.sourceHandle && connection.sourceHandle.startsWith('button-')) {
                // Extrai o ID do botão do sourceHandle (ex: "button-3" -> 3)
                toButtonId = Number(connection.sourceHandle.replace('button-', ''));
            }

            // Validação: wait_click só aceita source com botões URL e
            // sem postback (postback tem fluxo nativo via to_button_id).
            const targetNode = nodes.find((n) => n.id === connection.target);
            if (targetNode?.data.node_type === 'wait_click') {
                const sourceNode = nodes.find((n) => n.id === connection.source);
                const buttons = (sourceNode?.data?.buttons || sourceNode?.data?.config?.buttons || []) as Array<{ button_type?: string }>;
                const hasPostback = buttons.some((b) => b.button_type === 'postback');
                const hasUrl = buttons.some((b) => b.button_type === 'url');
                if (hasPostback) {
                    toast({
                        variant: 'destructive',
                        title: 'Conexão inválida',
                        description: 'Wait For Click só funciona com botões URL. Remova os botões de Ação (postback) do node de origem.',
                    });
                    return;
                }
                if (!hasUrl) {
                    toast({
                        variant: 'destructive',
                        title: 'Origem sem botões URL',
                        description: 'Wait For Click precisa que o node de origem tenha ao menos um botão URL.',
                    });
                    return;
                }
            }

            setEdges((eds) => addEdge(connection, eds));

            // ✅ Enviar to_button_id quando for conexão de botão
            createTransitionMutation.mutate({
                from_node_id: Number(connection.source),
                to_node_id: Number(connection.target),
                to_button_id: toButtonId,
            });
        },
        [setEdges, createTransitionMutation, nodes, toast]
    );


    // Handler para deletar edge
    const onEdgesDelete = useCallback(
        (edgesToDelete: FlowEdge[]) => {
            edgesToDelete.forEach((edge) => {
                if (edge.data?.id) {
                    deleteTransitionMutation.mutate(edge.data.id);
                }
            });
        },
        [deleteTransitionMutation]
    );

    // Handler para quando o node para de ser arrastado
    const onNodeDragStop = useCallback(
        (_event: any, node: Node) => {
            updateNodeMutation.mutate({
                nodeId: Number(node.id),
                data: {
                    position_x: node.position.x,
                    position_y: node.position.y,
                },
            });
        },
        [updateNodeMutation]
    );

    // Handler para selecionar node
    const onNodeClick = useCallback((_event: React.MouseEvent, node: FlowNode) => {
        setSelectedNode(node);
        selectedNodeRef.current = node;
    }, []);

    // Handler para adicionar novo node NO CENTRO da viewport
    const handleAddNode = useCallback(
        (nodeType: NodeType) => {
            try {
                // Pega o container visível do ReactFlow
                const wrapper = document.querySelector('.react-flow');
                if (!wrapper) return;

                const rect = wrapper.getBoundingClientRect();
                // Converte o centro da tela visível para coordenadas do flow
                const center = reactFlowInstance.screenToFlowPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top + rect.height / 2,
                });

                const newNodeData: NodeCreate = {
                    node_type: nodeType,
                    node_name: nodeNames[nodeType] || 'Novo Node',
                    position_x: Math.round(center.x),
                    position_y: Math.round(center.y),
                    config: nodeConfigs[nodeType] || {},
                };

                createNodeMutation.mutate({ data: newNodeData });
            } catch (error) {
                console.error('Erro ao criar node:', error);
                const newNodeData: NodeCreate = {
                    node_type: nodeType,
                    node_name: nodeNames[nodeType] || 'Novo Node',
                    position_x: 100,
                    position_y: 100,
                    config: nodeConfigs[nodeType] || {},
                };
                createNodeMutation.mutate({ data: newNodeData });
            }
        },
        [createNodeMutation, reactFlowInstance]
    );

    // Handler para validar funil
    const handleValidate = useCallback(async () => {
        try {
            const result = await validateFunnel(Number(funnelId));
            if (result.valid) {
                toast({
                    title: 'Funil válido!',
                    description: 'O funil está pronto para ser ativado.',
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: 'Funil inválido',
                    description: result.errors.join(', '),
                });
            }
        } catch (error: any) {
            toast({
                variant: 'destructive',
                title: 'Erro ao validar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        }
    }, [funnelId, toast]);

    // Keyboard shortcuts: Ctrl+C (copiar), Ctrl+V (colar)
    useEffect(() => {
        const handleKeyDown = async (e: KeyboardEvent) => {
            const isCtrlOrCmd = e.ctrlKey || e.metaKey;
            if (!isCtrlOrCmd) return;
            if (e.key !== 'c' && e.key !== 'v') return;

            // Ignora se estiver digitando em input/textarea
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if ((e.target as HTMLElement).isContentEditable) return;

            // Ctrl+C — copiar node selecionado
            if (e.key === 'c') {
                const node = selectedNodeRef.current;
                if (!node) return;
                const data = node.data as FlowNodeData;
                if (data.node_type === 'trigger') return;
                e.preventDefault();
                clipboardRef.current = data;
                toast({ title: 'Node copiado!' });
            }

            // Ctrl+V — colar node
            if (e.key === 'v') {
                const copiedNode = clipboardRef.current;
                if (!copiedNode || !funnelId) return;
                e.preventDefault();

                try {
                    // Posicao no centro do viewport
                    const { x, y, zoom } = reactFlowInstance.getViewport();
                    const container = document.querySelector('.react-flow__viewport');
                    const bounds = container?.getBoundingClientRect();
                    const centerX = bounds ? Math.round((bounds.width / 2 - x) / zoom) : 300;
                    const centerY = bounds ? Math.round((bounds.height / 2 - y) / zoom) : 300;

                    // 1. Cria o node
                    const newNode = await createNode(Number(funnelId), {
                        node_type: copiedNode.node_type,
                        node_name: `${copiedNode.node_name} (cópia)`,
                        position_x: centerX,
                        position_y: centerY,
                        config: copiedNode.config || {},
                    });

                    // 2. Atualiza com config completo (message, title, media, etc)
                    const config = copiedNode.config || {};
                    if (Object.keys(config).length > 0) {
                        await updateNode(newNode.id, { config });
                    }

                    // 3. Cria botoes se houver
                    if (copiedNode.buttons?.length) {
                        for (const btn of copiedNode.buttons) {
                            await createButton(newNode.id, {
                                button_text: btn.button_text,
                                button_type: btn.button_type,
                                button_order: btn.button_order,
                                url: btn.url,
                            });
                        }
                    }

                    // 4. Refetch
                    queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
                    toast({ title: 'Node colado!' });
                } catch (error: any) {
                    toast({
                        variant: 'destructive',
                        title: 'Erro ao colar node',
                        description: error.response?.data?.detail || 'Ocorreu um erro.',
                    });
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [funnelId, reactFlowInstance, queryClient, toast]);

    if (isLoading) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0f172a]"></div>
            </div>
        );
    }

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex-shrink-0">
                <Toolbar
                    funnelName={builderMode === 'comment_auto_reply' ? (ruleData?.name || 'Auto-Reply') : (funnelData?.nome || 'Funil')}
                    onBack={onBack || (() => navigate('/meta/flow'))}
                    onValidate={handleValidate}
                    onPreview={() => setShowPreview(true)}
                    funnel={funnelData}
                    viewingPageId={viewingPageId}
                    onViewPageTranslation={setViewingPageId}
                />
            </div>

            {/* Banner de modo visualização */}
            {isViewing && (
                <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between text-amber-900 text-xs">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-200">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </span>
                        <span className="font-medium">
                            Visualizando tradução de <strong>{viewingPage?.page_name || viewingPageId}</strong> ({viewingPage?.language || '—'})
                        </span>
                        <span className="text-amber-700">— somente leitura. Para editar, saia da visualização.</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setViewingPageId(null)}
                        className="px-2.5 py-1 rounded bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700 transition-colors"
                    >
                        Sair da visualização
                    </button>
                </div>
            )}

            {/* Canvas Container */}
            <div className="flex-1 relative overflow-hidden">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onEdgesDelete={onEdgesDelete}
                    onNodeDragStop={onNodeDragStop}
                    onNodeClick={onNodeClick}
                    onPaneClick={() => setSelectedNode(null)}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    connectionMode={ConnectionMode.Loose}
                    connectionRadius={150}
                    fitView
                    fitViewOptions={{
                        padding: 0.2,
                        minZoom: 0.5,
                        maxZoom: 1.5,
                    }}
                    minZoom={0.1}
                    maxZoom={2}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                    className="bg-[#f8f9fb]"
                    defaultEdgeOptions={{
                        style: { stroke: '#2196F3', strokeWidth: 2 },
                        animated: false,
                        zIndex: 999
                    }}
                >
                    <Background
                        color="#d1d5db"
                        gap={20}
                        size={1.5}
                        style={{ backgroundColor: '#f8f9fb' }}
                    />
                    <Controls className="[&>button]:bg-white [&>button]:text-gray-600 [&>button]:border [&>button]:border-gray-200 [&>button]:shadow-sm [&>button]:hover:bg-gray-50" />
                </ReactFlow>

                {/* Sidebar Flutuante */}
                <Sidebar
                    onAddNode={handleAddNode}
                    categories={builderMode === 'comment_auto_reply' ? commentAutoReplyNodeCategories : undefined}
                />

                {/* Node Panel */}
                {selectedNode && (
                    <NodePanel
                        node={selectedNode}
                        onClose={() => setSelectedNode(null)}
                        page_id={funnelData?.page_id || funnelData?.pages?.[0]?.page_id || ''}
                        builderMode={builderMode}
                        ruleData={ruleData}
                        funnelPageIds={
                            builderMode === 'comment_auto_reply' && ruleData?.page_ids?.length
                                ? ruleData.page_ids
                                : funnelData?.pages?.map(p => p.page_id) || []
                        }
                    />
                )}

                <FlowPreviewModal
                    open={showPreview}
                    onClose={() => setShowPreview(false)}
                    nodes={(funnelData?.nodes as any[]) || []}
                    transitions={funnelData?.transitions || []}
                    pageName={funnelData?.nome}
                />
            </div>
        </div>
    );
}

// Componente EXTERNO (wrapper com Provider)
export default function FlowBuilder(props: FlowBuilderProps) {
    return (
        <ReactFlowProvider>
            <FlowBuilderInner {...props} />
        </ReactFlowProvider>
    );
}